import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as S from './styles';
import { useMessage } from 'newComponents/ChatWhatsapp/context/MessageContext';
import { CircularProgress } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import Button from 'newComponents/Button';
import CloseIcon from '@mui/icons-material/Close';
import { pipego } from 'api/pipego';
import { useNotifyAlert } from 'context/NotifyAlert';

// Tempo máximo que o modal fica bloqueado aguardando a conexão abrir depois da
// leitura do QR Code. Depois disso o usuário volta a poder fechar a janela.
const CONNECTING_TIMEOUT_MS = 60000;

const NewPhoneModal = ( {
	position,
	modalRef
} ) => {
	const {
		handleTogglePhoneNumberStatus,
		qrCode,
		setQrCode,
		setIsNewPhoneNumberModalOpen,
		subSessionToHandle,
		setSubSessionToHandle,
		handleRecreateSession,
		pendingSubSessionId,
		setPendingSubSessionId,
		connectionPhase,
		setConnectionPhase
	} = useMessage();
	const [ isLoadingNewQrCode, setIsLoadingNewQrCode ] = useState( false );
	const [ hasConnectingTimedOut, setHasConnectingTimedOut ] = useState( false );
	const { setShowNotify } = useNotifyAlert();

	// AFCO-417: entre a leitura do QR Code e a abertura da conexão o usuário não
	// tinha retorno nenhum, e acabava clicando de novo ou fechando o modal — o que
	// apagava no backend justamente a sub-sessão recém-conectada.
	const isConnecting = connectionPhase === 'connecting';
	const isModalLocked = isConnecting && !hasConnectingTimedOut;

	useEffect( () => {
		if ( !isConnecting ) {
			setHasConnectingTimedOut( false );
			return undefined;
		}
		const timer = setTimeout( () => setHasConnectingTimedOut( true ), CONNECTING_TIMEOUT_MS );
		return () => clearTimeout( timer );
	}, [ isConnecting ] );

	const handlerRecreateSession = async () => {
		if ( isLoadingNewQrCode || isModalLocked ) {
			return;
		}
		setIsLoadingNewQrCode( true );
		try {
			if( subSessionToHandle ) {
				await handleTogglePhoneNumberStatus( subSessionToHandle.id );
			} else {
				await handleRecreateSession();
			}
		} finally {
			setIsLoadingNewQrCode( false );
		}
	};

	const handlerCloseModal = async (removeQrCode = false) => {
		if ( isModalLocked ) {
			return;
		}
		setIsNewPhoneNumberModalOpen( false );
		const subSessionIdToRemove = subSessionToHandle?.id ?? pendingSubSessionId;
		// Só cancela a geração do QR Code quando sabemos exatamente qual sub-sessão
		// remover. Antes era enviado um id vazio e o backend escolhia sozinho uma
		// sub-sessão sem número — podendo deslogar a que o usuário acabou de ler.
		if( removeQrCode && subSessionIdToRemove ) {
			try {
				await pipego.removeQrCodeGeneration( subSessionIdToRemove );
			} catch ( error ) {
				console.error( 'Erro ao cancelar a geração do QR Code:', error );
			}
		}
		setPendingSubSessionId( null );
		setConnectionPhase( 'idle' );
		setQrCode( null );
		await setSubSessionToHandle( null );
	};

	const handlerDeactivateConnection = async () => {
		try{
			await handleTogglePhoneNumberStatus( subSessionToHandle.id );
			setSubSessionToHandle( null );
			await handlerCloseModal( false );
			setShowNotify( {
				status : 'success',
				text   : 'Conexão do WhatsApp desativada com sucesso!'
			} );
		} catch (error) {
			console.error( 'Erro ao desativar conexão do WhatsApp:', error );
			setShowNotify( {
				status : 'error',
				text   : 'Erro ao desativar conexão do WhatsApp'
			} );
		}
	};

	if(setSubSessionToHandle && subSessionToHandle?.isActive) {
		return (
			<S.ModalContainer position={position} ref={modalRef}>
				<S.CloseButton onClick={() => handlerCloseModal( false )}>
					<CloseIcon />
				</S.CloseButton>
					<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}>
						<p>Desativar conexão do WhatsApp no numero selecionado?</p>
						<S.ButtonsContainer>
							<Button type="button" onClick={() => handlerCloseModal( false )}>
								Cancelar
							</Button>
							<Button type="button" onClick={() => handlerDeactivateConnection( )}>
								Desativar conexão
							</Button>
						</S.ButtonsContainer>
					</div>
			</S.ModalContainer>
		);
	}

	if( isConnecting ) {
		return (
			<S.ModalContainer position={position} ref={modalRef}>
				{hasConnectingTimedOut && (
					<S.CloseButton onClick={() => handlerCloseModal( false )}>
						<CloseIcon />
					</S.CloseButton>
				)}
				<S.ConnectingContainer>
					<CircularProgress />
					<S.ConnectingTitle>QR Code lido com sucesso!</S.ConnectingTitle>
					<S.WaitingQrCodeContent>
						Estabelecendo a conexão com o WhatsApp. Isso pode levar alguns segundos.
					</S.WaitingQrCodeContent>
					{hasConnectingTimedOut ? (
						<S.WaitingQrCodeContent>
							A conexão está demorando mais que o esperado. Você pode fechar esta janela e
							conferir o número na lista de telefones.
						</S.WaitingQrCodeContent>
					) : (
						<S.WaitingQrCodeContent>
							Não feche esta janela: o modal fecha sozinho assim que a conexão for concluída.
						</S.WaitingQrCodeContent>
					)}
				</S.ConnectingContainer>
			</S.ModalContainer>
		);
	}

	return (
		<S.ModalContainer position={position} ref={modalRef}>
			<S.CloseButton onClick={() => handlerCloseModal(true)}>
				<CloseIcon />
			</S.CloseButton>
				<>
					{qrCode ? (
						<>
							{!isLoadingNewQrCode ? <S.QrCodeContainer>
							<S.WaitingQrCodeContent>Por favor, escaneie o QR Code com seu WhatsApp para conectar:</S.WaitingQrCodeContent>
							<S.WaitingQrCodeContent>Aguardando leitura do QR Code...</S.WaitingQrCodeContent>
								<S.QrCodeContent
										src={qrCode}
										alt="QR Code"
									/>
								<S.WaitingQrCodeContent>
									Após a leitura, aguarde o modal fechar automaticamente para continuar.
								</S.WaitingQrCodeContent>
							</S.QrCodeContainer> : <S.LoadingContainer>
								<CircularProgress />
							</S.LoadingContainer>
							}
							<Button type="button" loading={isLoadingNewQrCode} onClick={() => handlerRecreateSession()}><Refresh />Solicitar novo QR Code</Button>
						</>
					) : (
						<>
							<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
								<p>Aguardando geração do QR Code...</p>
								<CircularProgress />
								{/* O botão só libera quando o QR Code anterior terminou de ser gerado. */}
								<Button type="button" loading disabled onClick={() => handlerRecreateSession()}><Refresh />Solicitar novo QR Code</Button>
							</div>
						</>
					)}
				</>
		</S.ModalContainer>
	);
};

NewPhoneModal.propTypes = {
	position : PropTypes.shape( {
		top  : PropTypes.number.isRequired,
		left : PropTypes.number.isRequired
	} ).isRequired,
	modalRef                  : PropTypes.object.isRequired,
};

export default NewPhoneModal;
